import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createVideoGeneration, buildVideoGenerationRequestBody, type VideoGenerationPayload } from '../../api/seedance';
import type {
  MatNode,
  MediaType,
  PromptItem,
  PromptMentionCandidate,
  Task,
  TaskContext,
  TaskStatus,
} from '../../store/seedanceTypes';
import {
  DEFAULT_SEEDANCE_MODEL,
  persistSeedanceWorkspaceState,
  useSeedanceWorkspaceStore,
} from '../../store/seedanceWorkspaceStore';
import {
  VIDEO_GENERATION_BASE_URL,
  WORKSPACE_MAX_PROMPTS,
  applySubtitleDirectiveToPrompt,
  getWorkspaceMaterialLimits,
} from '../../utils/seedanceConstants';
import {
  buildVideoGenerationMetadataItems,
  jsonStringifyForDebug,
} from '../../utils/seedanceGenerationUtils';
import {
  adjustPromptItemsAfterSlotInserted,
  adjustPromptItemsAfterSlotRemoved,
  prefixActiveNamedMaterials,
  buildPromptMentionLabel,
  findPromptMentionCandidate,
  getConnectedMentionIndexForNode,
  removeNamedPromptMentionAtIndex,
  getMentionIndexOnConnect,
  getPromptMentionAtIndex,
  getTextareaIndexNearPoint,
  promptTextHasMediaRefSegments,
  removeNamedPromptMention,
  replaceNamedPromptMentionsForSubmit,
  stripPromptMentionsForSubmit,
} from '../../utils/seedancePromptUtils';
import { extractApiMessage } from '../../utils/seedanceRequestUtils';
import {
  SEEDANCE_STORAGE_KEYS,
  getLocalStorageValue,
  loadSeedanceWorkspaceIndex,
  migrateSeedanceWorkspaceIndex,
  type SeedanceWorkspaceIndex,
} from '../../utils/seedanceStorage';
import {
  loadCachedMatPortraitPatches,
  refreshMatNodesPortraitUrls,
} from '../../utils/seedanceWorkspaceMatRefresh';
import {
  disconnectMissingAssetNodes,
  formatMissingAssetSubmitMessage,
  probeMissingConnectedAssetIds,
} from '../../utils/seedanceWorkspaceAssetIds';
import { normalizeStatus } from '../../utils/seedanceTaskUtils';
import {
  tryBeginVideoGeneration,
  VIDEO_GENERATION_RATE_LIMIT_MESSAGE,
} from '../../utils/videoGenerationRateLimit';
import { useSeedanceAuthStore } from '../../store/seedanceAuthStore';
import { useSeedanceLibraryStore } from '../../store/seedanceLibraryStore';
import { useSeedanceTaskStore } from '../../store/seedanceTaskStore';
import { useSeedanceUiStore } from '../../store/seedanceUiStore';
import { ConfirmModal } from '../../components/ConfirmModal';
import VideoAssetCard from '../../components/VideoAssetCard';
import { PromptTextHighlight } from '../../components/PromptTextHighlight';
import { usePromptHighlightOverlayBindings } from '../../hooks/usePromptHighlightOverlayBindings';
import { promptHighlightOverlayHandlers } from '../../utils/promptHighlightOverlay';
import { MaterialNodeCard, ICONS } from './components/MaterialNodeCard/MaterialNodeCard';
import './workspace.css';
import { Space } from 'antd';
import { ScanOutlined } from '@ant-design/icons';


export interface WorkspacePageOutletContext {
  counts: {
    activeImgs: number;
    activeVids: number;
    activeAuds: number;
    prompts: number;
  };
  prompts: PromptItem[];
  nodes: MatNode[];
  draggingId: string | null;
  dragOverId: string | null;
  promptHighlightRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  promptInputRefs: React.MutableRefObject<Record<number, HTMLTextAreaElement | null>>;
  mentionState: { promptId: number | null; query: string };
  filteredMentionCandidates: PromptMentionCandidate[];
  connectedNodes: MatNode[];
  model: string;
  duration: number;
  ratio: string;
  resolution: string;
  subtitleEnabled: boolean;
  clearAll: () => void;
  addPromptNode: () => number;
  removePromptNode: (id: number) => void;
  handlePromptChangeWithMention: (id: number, text: string, cursorPos: number) => void;
  identifyPromptMaterials: (id: number) => void;
  setMentionState: React.Dispatch<React.SetStateAction<{ promptId: number | null; query: string }>>;
  onPromptMouseMoveMentionThumb: (e: React.MouseEvent<HTMLTextAreaElement>) => void;
  onPromptMouseLeaveMentionThumb: () => void;
  onPromptNamedMentionClick: (promptId: number, e: React.MouseEvent<HTMLTextAreaElement>) => void;
  applyPromptMention: (promptId: number, candidate: PromptMentionCandidate) => void;
  clearPrompt: (id: number) => void;
  onNodeDragStart: (id: string) => (e: React.DragEvent) => void;
  onNodeDragEnd: () => void;
  onNodeDragOver: (id: string) => (e: React.DragEvent) => void;
  onNodeDragLeave: () => void;
  onNodeDrop: (targetId: string) => (e: React.DragEvent) => void;
  removeNode: (id: string) => void;
  openLibPicker: (nodeId: string) => void;
  updateNodeName: (id: string, val: string) => void;
  finalizeNodeName: (id: string) => void;
  toggleConnect: (id: string) => void;
  /** 图片槽预览加载失败（多为外链过期），按节点重拉虚拟人像详情中的新地址 */
  onMatPreviewImageError: (nodeId: string) => void;
  addMatNode: (type: MediaType) => MatNode;
  setModel: (model: string) => void;
  setDuration: (duration: number) => void;
  setRatio: (ratio: string) => void;
  setResolution: (resolution: string) => void;
  setSubtitleEnabled: (enabled: boolean) => void;
  generateVideo: () => void;
  mentionThumbHover: {
    thumbUrl: string | null;
    label: string;
    x: number;
    y: number;
  } | null;
}

type WorkspacePageBaseContext = Omit<WorkspacePageOutletContext, 'openLibPicker'>;

interface UseWorkspacePageControllerArgs {
}

const readWorkspaceStatePersistenceKey = () => {
  const state = useSeedanceWorkspaceStore.getState();
  return JSON.stringify({
    nodes: state.nodes,
    prompts: state.prompts,
    nodeSeq: state.nodeSeq,
    ratio: state.ratio,
    resolution: state.resolution,
    duration: state.duration,
    model: state.model,
    subtitleEnabled: state.subtitleEnabled,
  });
};

/** 工作台模块自己的节点、提示词、拖拽、持久化和生成逻辑。 */
export const useWorkspacePageController = ({ }: UseWorkspacePageControllerArgs = {}) => {
  const apiKey = useSeedanceAuthStore((s) => s.apiKey);
  const connected = useSeedanceAuthStore((s) => s.connected);
  const configUserId = useSeedanceAuthStore((s) => s.configUserId);
  const libSelectedGroupId = useSeedanceLibraryStore((s) => s.libSelectedGroupId);
  const apiKeyRef = useRef(apiKey);
  useEffect(() => { apiKeyRef.current = apiKey; }, [apiKey]);

  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const ensurePolling = useSeedanceTaskStore((s) => s.ensurePolling);

  const addMatNode = useSeedanceWorkspaceStore((s) => s.addMatNode);
  const applyLibAssetToNode = useSeedanceWorkspaceStore((s) => s.applyLibAssetToNode);
  const nodeSeq = useSeedanceWorkspaceStore((s) => s.nodeSeq);
  const setNodeSeq = useSeedanceWorkspaceStore((s) => s.setNodeSeq);
  const nodes = useSeedanceWorkspaceStore((s) => s.nodes);
  const setNodes = useSeedanceWorkspaceStore((s) => s.setNodes);
  const prompts = useSeedanceWorkspaceStore((s) => s.prompts);
  const setPrompts = useSeedanceWorkspaceStore((s) => s.setPrompts);
  const duration = useSeedanceWorkspaceStore((s) => s.duration);
  const setDuration = useSeedanceWorkspaceStore((s) => s.setDuration);
  const ratio = useSeedanceWorkspaceStore((s) => s.ratio);
  const setRatio = useSeedanceWorkspaceStore((s) => s.setRatio);
  const resolution = useSeedanceWorkspaceStore((s) => s.resolution);
  const setResolution = useSeedanceWorkspaceStore((s) => s.setResolution);
  const model = useSeedanceWorkspaceStore((s) => s.model);
  const setModel = useSeedanceWorkspaceStore((s) => s.setModel);
  const subtitleEnabled = useSeedanceWorkspaceStore((s) => s.subtitleEnabled);
  const setSubtitleEnabled = useSeedanceWorkspaceStore((s) => s.setSubtitleEnabled);
  const setTasks = useSeedanceTaskStore((s) => s.setTasks);

  const promptSeqRef = useRef(1);
  const loadedRef = useRef(false);

  const dragNodeIdRef = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [clearWorkspaceConfirmOpen, setClearWorkspaceConfirmOpen] = useState(false);
  const promptInputRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const promptHighlightRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const promptCursorRef = useRef<Record<number, number>>({});
  const nodeNameRef = useRef<Record<string, string>>({});
  const [mentionState, setMentionState] = useState<{ promptId: number | null; query: string }>({
    promptId: null,
    query: '',
  });
  const mentionThumbHover = useSeedanceWorkspaceStore((s) => s.mentionThumbHover);
  const setMentionThumbHover = useSeedanceWorkspaceStore((s) => s.setMentionThumbHover);
  const mentionThumbHoverRaf = useRef<number | null>(null);

  usePromptHighlightOverlayBindings(
    prompts.map((p) => p.id),
    prompts.map((p) => p.text),
    promptInputRefs,
    promptHighlightRefs,
  );

  /** 工作台索引恢复后再跑素材直链刷新；预览地址不是持久化数据。 */
  const [workspaceHydrated, setWorkspaceHydrated] = useState(false);
  const portraitFlushAbortRef = useRef<AbortController | null>(null);
  const portraitFlushSeqRef = useRef(0);
  const portraitFlushTimerRef = useRef<number | null>(null);
  const matPortraitRetryAbortRef = useRef<Map<string, AbortController>>(new Map());
  const matPortraitRetryTsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const applyWorkspaceIndex = (index: SeedanceWorkspaceIndex) => {
        setRatio(index.params.ratio || '16:9');
        setResolution(index.params.resolution || '720p');
        setDuration(index.params.duration || 10);
        setModel(index.params.model || DEFAULT_SEEDANCE_MODEL);
        setSubtitleEnabled(Boolean(index.params.subtitleEnabled));
        setNodes(
          index.nodes.map((node) => ({
            ...node,
            url: '',
            thumb: '',
          })),
        );
        setNodeSeq(index.nodeSeq || 1);
        if (index.prompts.length) {
          setPrompts(index.prompts);
          promptSeqRef.current = Math.max(...index.prompts.map((p) => p.id + 1), 1);
        }
      };

      // Normal startup is synchronous in the store. This branch only migrates
      // users who still have the pre-index IndexedDB workspace snapshot.
      if (!loadSeedanceWorkspaceIndex()) {
        const baseline = readWorkspaceStatePersistenceKey();
        const legacy = await migrateSeedanceWorkspaceIndex();
        if (cancelled) return;
        const stateChangedDuringMigration = readWorkspaceStatePersistenceKey() !== baseline;
        if (legacy && !stateChangedDuringMigration) {
          applyWorkspaceIndex(legacy);
        } else if (legacy || stateChangedDuringMigration) {
          // Do not let a delayed one-time migration overwrite a user edit.
          void persistSeedanceWorkspaceState();
        }
      }

      loadedRef.current = true;
      setWorkspaceHydrated(true);
      addDebug('首页科技 v1.0 本地版初始化完成');
    })();
    return () => {
      cancelled = true;
      if (mentionThumbHoverRaf.current != null) cancelAnimationFrame(mentionThumbHoverRaf.current);
    };
  }, [addDebug, setDuration, setModel, setNodes, setNodeSeq, setPrompts, setRatio, setResolution, setSubtitleEnabled]);

  useEffect(() => {
    if (!loadedRef.current) return;
    void persistSeedanceWorkspaceState();
  }, [nodes, prompts, ratio, resolution, duration, model, subtitleEnabled]);

  const nodesNeedingPortraitUrl = useCallback((nodeList: MatNode[]) => {
    return nodeList.filter((n) => {
      const sid = (n.sourceAssetId || '').trim();
      return (
        sid &&
        (n.type === 'image' || n.type === 'video' || n.type === 'audio') &&
        !(n.url || '').trim()
      );
    });
  }, []);

  const flushWorkspacePortraitUrlsFromStore = useCallback(() => {
    const targets = nodesNeedingPortraitUrl(useSeedanceWorkspaceStore.getState().nodes);
    if (!targets.length) return;

    portraitFlushAbortRef.current?.abort();
    const seq = ++portraitFlushSeqRef.current;
    const ac = new AbortController();
    portraitFlushAbortRef.current = ac;

    void (async () => {
      try {
        const applyPatch = (patch: Map<string, { sourceAssetId: string; url: string; thumb: string }>) => {
          if (ac.signal.aborted || seq !== portraitFlushSeqRef.current || patch.size === 0) return;
          setNodes((prev) =>
            prev.map((node) => {
              const u = patch.get(node.id);
              if (!u || u.sourceAssetId !== (node.sourceAssetId || '').trim()) return node;
              return {
                ...node,
                ...(u.url ? { url: u.url } : {}),
                ...(u.thumb ? { thumb: u.thumb } : {}),
              };
            }),
          );
        };

        const cachedPatch = await loadCachedMatPortraitPatches(targets, { onDebug: addDebug });
        if (ac.signal.aborted || seq !== portraitFlushSeqRef.current) return;
        applyPatch(cachedPatch);

        const remaining = targets.filter((node) => !(cachedPatch.get(node.id)?.url || '').trim());
        if (!remaining.length) return;
        const token = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey).trim();
        const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
        if (!token || !userId) return;

        const patch = await refreshMatNodesPortraitUrls(remaining, {
          onDebug: addDebug,
          signal: ac.signal,
        });
        applyPatch(patch);
      } catch {
        /* 本地缓存或网络失败时保留已有节点外壳 */
      }
    })();
  }, [addDebug, nodesNeedingPortraitUrl, setNodes]);

  const scheduleWorkspacePortraitFlush = useCallback(
    (delayMs = 350) => {
      if (portraitFlushTimerRef.current != null) {
        window.clearTimeout(portraitFlushTimerRef.current);
      }
      portraitFlushTimerRef.current = window.setTimeout(() => {
        portraitFlushTimerRef.current = null;
        flushWorkspacePortraitUrlsFromStore();
      }, delayMs);
    },
    [flushWorkspacePortraitUrlsFromStore],
  );

  const onMatPreviewImageError = useCallback(
    (nodeId: string) => {
      const n = useSeedanceWorkspaceStore.getState().nodes.find((x) => x.id === nodeId);
      if (!n || n.type !== 'image') return;
      const sid = (n.sourceAssetId || '').trim();
      if (!sid) return;

      const now = Date.now();
      const last = matPortraitRetryTsRef.current[nodeId] ?? 0;
      if (now - last < 45_000) return;
      matPortraitRetryTsRef.current[nodeId] = now;

      matPortraitRetryAbortRef.current.get(nodeId)?.abort();
      const ac = new AbortController();
      matPortraitRetryAbortRef.current.set(nodeId, ac);

      void (async () => {
        try {
          const snapshot = useSeedanceWorkspaceStore.getState().nodes.find((x) => x.id === nodeId);
          if (!snapshot || (snapshot.sourceAssetId || '').trim() !== sid) return;
          const patch = await refreshMatNodesPortraitUrls([snapshot], {
            onDebug: addDebug,
            signal: ac.signal,
          });
          if (ac.signal.aborted) return;
          if (patch.size === 0) return;
          setNodes((prev) =>
            prev.map((node) => {
              const u = patch.get(node.id);
              if (!u) return node;
              if (u.sourceAssetId !== (node.sourceAssetId || '').trim()) return node;
              return {
                ...node,
                ...(u.url ? { url: u.url } : {}),
                ...(u.thumb ? { thumb: u.thumb } : {}),
              };
            }),
          );
        } catch {
          /* noop */
        } finally {
          if (matPortraitRetryAbortRef.current.get(nodeId) === ac) {
            matPortraitRetryAbortRef.current.delete(nodeId);
          }
        }
      })();
    },
    [addDebug, setNodes],
  );

  useEffect(() => {
    return () => {
      if (portraitFlushTimerRef.current != null) {
        window.clearTimeout(portraitFlushTimerRef.current);
      }
      portraitFlushAbortRef.current?.abort();
      matPortraitRetryAbortRef.current.forEach((ac) => ac.abort());
      matPortraitRetryAbortRef.current.clear();
    };
  }, []);

  const nodesNeedPortraitRefresh = useMemo(
    () => nodesNeedingPortraitUrl(nodes).length > 0,
    [nodes, nodesNeedingPortraitUrl],
  );

  useEffect(() => {
    if (!workspaceHydrated || !nodesNeedPortraitRefresh) return;
    scheduleWorkspacePortraitFlush(350);
    return () => {
      if (portraitFlushTimerRef.current != null) {
        window.clearTimeout(portraitFlushTimerRef.current);
        portraitFlushTimerRef.current = null;
      }
    };
  }, [
    workspaceHydrated,
    apiKey,
    configUserId,
    libSelectedGroupId,
    nodesNeedPortraitRefresh,
    scheduleWorkspacePortraitFlush,
  ]);

  useEffect(() => {
    if (!workspaceHydrated) return;
    let tid: number | undefined;
    const schedule = () => {
      if (document.visibilityState !== 'visible') return;
      const needs = nodesNeedingPortraitUrl(useSeedanceWorkspaceStore.getState().nodes).length > 0;
      if (!needs) return;
      if (tid !== undefined) window.clearTimeout(tid);
      tid = window.setTimeout(() => scheduleWorkspacePortraitFlush(650), 0);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') schedule();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', schedule);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', schedule);
      if (tid !== undefined) window.clearTimeout(tid);
    };
  }, [workspaceHydrated, nodesNeedingPortraitUrl, scheduleWorkspacePortraitFlush]);

  const addPromptNode = useCallback((forcedId: number | null = null, initialText = '') => {
    const id = forcedId !== null ? forcedId : promptSeqRef.current++;
    if (forcedId !== null && forcedId >= promptSeqRef.current) promptSeqRef.current = forcedId + 1;
    setPrompts((prev) => {
      if (prev.find((p) => p.id === id)) return prev;
      if (forcedId === null && prev.length >= WORKSPACE_MAX_PROMPTS) {
        showToast(`最多添加 ${WORKSPACE_MAX_PROMPTS} 个提示词`, 'e');
        return prev;
      }
      return [...prev, { id, text: initialText }];
    });
    return id;
  }, [setPrompts, showToast]);

  const removePromptNode = useCallback((id: number) => {
    setPrompts((prev) => prev.filter((p) => p.id !== id));
  }, [setPrompts]);

  const updatePromptText = useCallback((id: number, text: string) => {
    setPrompts((prev) => prev.map((p) => (p.id === id ? { ...p, text } : p)));
  }, [setPrompts]);

  const clearPrompt = useCallback((id: number) => {
    updatePromptText(id, '');
  }, [updatePromptText]);

  const mentionCandidates = useMemo<PromptMentionCandidate[]>(() => {
    const active = nodes.filter(
      (n) =>
        (n.type === 'image' || n.type === 'video' || n.type === 'audio') && n.connected,
    );
    let imageIdx = 0;
    let videoIdx = 0;
    let audioIdx = 0;
    return active.map((n) => {
      const label =
        n.type === 'video'
          ? buildPromptMentionLabel('video', ++videoIdx)
          : n.type === 'audio'
            ? buildPromptMentionLabel('audio', ++audioIdx)
            : buildPromptMentionLabel('image', ++imageIdx);
      return {
        nodeId: n.id,
        name: n.name,
        label,
        mention: `@${label}`,
      };
    });
  }, [nodes]);

  const filteredMentionCandidates = useMemo(() => {
    if (mentionState.promptId === null) return [];
    const q = mentionState.query.trim();
    if (!q) return mentionCandidates;
    return mentionCandidates.filter(
      (c) => c.label.toLowerCase().includes(q.toLowerCase()) || c.name.toLowerCase().includes(q.toLowerCase()),
    );
  }, [mentionCandidates, mentionState]);

  const handlePromptChangeWithMention = useCallback(
    (id: number, text: string, cursorPos: number) => {
      promptCursorRef.current[id] = cursorPos;
      updatePromptText(id, text);

      const before = text.slice(0, cursorPos);
      const m = /@([^\s@]*)$/.exec(before);
      if (!m || mentionCandidates.length === 0) {
        if (mentionState.promptId === id) setMentionState({ promptId: null, query: '' });
        return;
      }
      setMentionState({ promptId: id, query: m[1] ?? '' });
    },
    [
      mentionCandidates.length,
      mentionState.promptId,
      updatePromptText,
    ],
  );

  const identifyPromptMaterials = useCallback((id: number) => {
    setPrompts((prev) =>
      prev.map((prompt) => {
        if (prompt.id !== id) return prompt;
        const result = prefixActiveNamedMaterials(prompt.text, nodes);
        return result.text === prompt.text ? prompt : { ...prompt, text: result.text };
      }),
    );
  }, [nodes, setPrompts]);

  const applyPromptMention = useCallback((promptId: number, candidate: PromptMentionCandidate) => {
    const input = promptInputRefs.current[promptId];
    const fallbackCursor = input?.selectionStart ?? 0;
    const cursor = promptCursorRef.current[promptId] ?? fallbackCursor;
    setPrompts((prev) =>
      prev.map((p) => {
        if (p.id !== promptId) return p;
        const before = p.text.slice(0, cursor);
        const after = p.text.slice(cursor);
        const replacedBefore = before.replace(/@([^\s@]*)$/, `${candidate.mention} `);
        const nextText = replacedBefore + after;
        const nextCursor = replacedBefore.length;
        promptCursorRef.current[promptId] = nextCursor;
        window.setTimeout(() => {
          const el = promptInputRefs.current[promptId];
          if (!el) return;
          el.focus();
          el.setSelectionRange(nextCursor, nextCursor);
        }, 0);
        return { ...p, text: nextText };
      }),
    );
    setMentionState({ promptId: null, query: '' });
  }, [setPrompts]);

  const flushMentionThumbHover = useCallback(
    (ta: HTMLTextAreaElement, clientX: number, clientY: number) => {
      const text = ta.value;
      if (!promptTextHasMediaRefSegments(text)) {
        setMentionThumbHover(null);
        return;
      }
      const idx = text.length > 8000 ? -1 : getTextareaIndexNearPoint(ta, clientX, clientY);
      const ref = idx >= 0 ? getPromptMentionAtIndex(text, idx) : null;
      if (ref == null) {
        setMentionThumbHover(null);
        return;
      }
      const cand = findPromptMentionCandidate(mentionCandidates, ref);
      const label = cand
        ? cand.mention
        : `@${buildPromptMentionLabel(ref.kind, ref.num)}`;
      if (!cand) {
        setMentionThumbHover({ thumbUrl: null, label, x: clientX, y: clientY });
        return;
      }
      const node = nodes.find((n) => n.id === cand.nodeId);
      const thumbUrl = (node?.thumb || node?.url || '').trim() || null;
      setMentionThumbHover({ thumbUrl, label, x: clientX, y: clientY });
    },
    [mentionCandidates, nodes],
  );

  const onPromptMouseMoveMentionThumb = useCallback(
    (e: React.MouseEvent<HTMLTextAreaElement>) => {
      const ta = e.currentTarget;
      const cx = e.clientX;
      const cy = e.clientY;
      if (mentionThumbHoverRaf.current != null) cancelAnimationFrame(mentionThumbHoverRaf.current);
      mentionThumbHoverRaf.current = requestAnimationFrame(() => {
        mentionThumbHoverRaf.current = null;
        if (!ta?.isConnected) return;
        flushMentionThumbHover(ta, cx, cy);
      });
    },
    [flushMentionThumbHover],
  );

  const onPromptMouseLeaveMentionThumb = useCallback(() => {
    if (mentionThumbHoverRaf.current != null) {
      cancelAnimationFrame(mentionThumbHoverRaf.current);
      mentionThumbHoverRaf.current = null;
    }
    setMentionThumbHover(null);
  }, [setMentionThumbHover]);

  const removeNode = useCallback(
    (id: string) => {
      const target = nodes.find((node) => node.id === id);
      const ref = getConnectedMentionIndexForNode(nodes, id);
      setNodes((prev) => prev.filter((x) => x.id !== id));
      void persistSeedanceWorkspaceState({ allowEmpty: nodes.length === 1 });
      if (ref || target?.name.trim()) {
        setPrompts((prev) => {
          const adjusted = ref
            ? adjustPromptItemsAfterSlotRemoved(prev, ref.kind, ref.num)
            : prev;
          return target?.name.trim()
            ? adjusted.map((prompt) => ({
                ...prompt,
                text: removeNamedPromptMention(prompt.text, target.name),
              }))
            : adjusted;
        });
      }
    },
    [nodes, setNodes, setPrompts],
  );

  const updateNodeName = useCallback((id: string, val: string) => {
    const previousName = (nodeNameRef.current[id] || nodes.find((node) => node.id === id)?.name || '').trim();
    const nextName = val.trim();
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, name: val } : n)));
    if (previousName && nextName && previousName !== nextName) {
      setPrompts((prev) =>
        prev.map((prompt) => ({
          ...prompt,
          text: prompt.text.split(`@${previousName}`).join(`@${nextName}`),
        })),
      );
    }
    if (nextName) nodeNameRef.current[id] = nextName;
  }, [nodes, setNodes, setPrompts]);

  const finalizeNodeName = useCallback((id: string) => {
    const currentName = nodes.find((node) => node.id === id)?.name.trim() || '';
    const previousName = (nodeNameRef.current[id] || currentName).trim();
    const nextName = currentName || '未命名';
    setNodes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, name: n.name.trim() || '未命名' } : n)),
    );
    if (previousName && nextName !== previousName) {
      setPrompts((prev) =>
        prev.map((prompt) => ({
          ...prompt,
          text: prompt.text.split(`@${previousName}`).join(`@${nextName}`),
        })),
      );
    }
    nodeNameRef.current[id] = nextName;
  }, [nodes, setNodes, setPrompts]);

  const toggleConnect = useCallback(
    (id: string) => {
      const target = nodes.find((n) => n.id === id);
      if (!target) return;

      if (target.connected) {
        const ref = getConnectedMentionIndexForNode(nodes, id);
        setNodes((prev) =>
          prev.map((n) => (n.id === id ? { ...n, connected: false } : n)),
        );
        if (ref || target.name.trim()) {
          setPrompts((prev) => {
            const adjusted = ref
              ? adjustPromptItemsAfterSlotRemoved(prev, ref.kind, ref.num)
              : prev;
            return target.name.trim()
              ? adjusted.map((prompt) => ({
                  ...prompt,
                  text: removeNamedPromptMention(prompt.text, target.name),
                }))
              : adjusted;
          });
        }
        return;
      }

      const activeImgs = nodes.filter((n) => n.type === 'image' && n.connected).length;
      const activeVids = nodes.filter((n) => n.type === 'video' && n.connected).length;
      const activeAuds = nodes.filter((n) => n.type === 'audio' && n.connected).length;

      const materialLimits = getWorkspaceMaterialLimits(model);
      const maxImgs = materialLimits.images;
      const maxVids = materialLimits.videos;
      const maxAuds = materialLimits.audios;

      if (target.type === 'image' && activeImgs >= maxImgs) {
        showToast(`最多激活 ${maxImgs} 张图片`, 'e');
        return;
      }
      if (target.type === 'video' && activeVids >= maxVids) {
        showToast(`最多激活 ${maxVids} 个视频`, 'e');
        return;
      }
      if (target.type === 'audio' && activeAuds >= maxAuds) {
        showToast(`最多激活 ${maxAuds} 个音频`, 'e');
        return;
      }

      const insertRef = getMentionIndexOnConnect(nodes, id);
      setNodes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, connected: true } : n)),
      );
      if (insertRef) {
        setPrompts((prev) =>
          adjustPromptItemsAfterSlotInserted(prev, insertRef.kind, insertRef.num),
        );
      }
    },
    [nodes, setNodes, setPrompts, showToast, model],
  );

  const onPromptNamedMentionClick = useCallback(
    (promptId: number, e: React.MouseEvent<HTMLTextAreaElement>) => {
      const index = getTextareaIndexNearPoint(e.currentTarget, e.clientX, e.clientY);
      const text = e.currentTarget.value;
      const nextText = removeNamedPromptMentionAtIndex(
        text,
        index,
        nodes.filter((node) => node.connected).map((node) => node.name),
      );
      if (nextText === text) return;
      setPrompts((prev) =>
        prev.map((prompt) => (prompt.id === promptId ? { ...prompt, text: nextText } : prompt)),
      );
    },
    [nodes, setPrompts],
  );

  const generateVideo = useCallback(async () => {
    if (!connected) {
      showToast('请先连接 API Key', 'e');
      return;
    }
    const limits = getWorkspaceMaterialLimits(model);
    const activeCounts = {
      images: nodes.filter((node) => node.connected && node.type === 'image').length,
      videos: nodes.filter((node) => node.connected && node.type === 'video').length,
      audios: nodes.filter((node) => node.connected && node.type === 'audio').length,
    };
    const overLimit = ([
      ['图片', activeCounts.images, limits.images],
      ['视频', activeCounts.videos, limits.videos],
      ['音频', activeCounts.audios, limits.audios],
    ] as const).filter(([, count, limit]) => count > limit);
    if (overLimit.length > 0) {
      showToast(
        `当前模型素材上限不足：${overLimit
          .map(([label, count, limit]) => `${label} ${count}/${limit}`)
          .join('、')}。请减少提示词中的素材引用或手动取消激活`,
        'e',
      );
      return;
    }
    const allPrompts = prompts
      .map((p) =>
        stripPromptMentionsForSubmit(
          replaceNamedPromptMentionsForSubmit(p.text.trim(), nodes),
        ),
      )
      .filter(Boolean);
    if (allPrompts.length === 0) {
      showToast('请至少输入一个提示词', 'e');
      return;
    }
    const missingAssets = await probeMissingConnectedAssetIds(nodes, {
      onDebug: addDebug,
    });
    if (missingAssets.length) {
      setNodes((prev) =>
        disconnectMissingAssetNodes(
          prev,
          missingAssets.map((item) => item.assetId),
        ),
      );
      showToast(formatMissingAssetSubmitMessage(missingAssets), 'e');
      return;
    }
    if (!tryBeginVideoGeneration()) {
      showToast(VIDEO_GENERATION_RATE_LIMIT_MESSAGE, 'i');
      return;
    }
    let mediaRef = '';
    const userPrompt = allPrompts.join('\n');
    const promptWithSubtitle = applySubtitleDirectiveToPrompt(userPrompt, subtitleEnabled);
    const prompt = mediaRef ? `${mediaRef}\n${promptWithSubtitle}` : promptWithSubtitle;

    const contextSnapshot: TaskContext = {
      prompts: allPrompts,
      nodes: JSON.parse(JSON.stringify(nodes)) as MatNode[],
      ratio,
      resolution,
      duration,
    };
    const localId = 'pending_' + Date.now();
    const localTask: Task = {
      id: localId,
      prompt,
      status: 'pending',
      video_url: null,
      thumbnail_url: null,
      created_at: new Date().toISOString(),
      model,
      duration,
      ratio,
      resolution,
      error: null,
      context: contextSnapshot,
    };
    setTasks((prev) => [localTask, ...prev]);

    try {
      addDebug('正在创建视频生成任务...');
      showToast('正在提交任务到API...', 'i');
      const connectedMatCount = nodes.filter(
        (n) =>
          (n.type === 'image' || n.type === 'video' || n.type === 'audio') && n.connected,
      ).length;
      const metadataItems = buildVideoGenerationMetadataItems(nodes);
      if (metadataItems.length) {
        addDebug(`将引用 ${metadataItems.length} 个已激活素材参与生成（content）`);
      } else if (connectedMatCount > 0) {
        addDebug('已连接素材中没有可用资源 ID，generations 请求将不携带参考素材', true);
      }

      const requestBody: VideoGenerationPayload = {
        model,
        prompt,
        duration,
        ratio,
        resolution,
        metadataItems,
      };

      const requestForApi = buildVideoGenerationRequestBody(requestBody);

      addDebug(`POST ${VIDEO_GENERATION_BASE_URL}/v1/video/generations`);
      addDebug(`Body: ${jsonStringifyForDebug(requestForApi)}…`);
      const result = await createVideoGeneration(requestBody, {
        baseUrl: VIDEO_GENERATION_BASE_URL,
        apiKey: apiKeyRef.current,
      });
      const resultData = result?.data && typeof result.data === 'object' ? result.data : result;
      const successFlag =
        typeof result?.success === 'boolean'
          ? result.success
          : typeof resultData?.success === 'boolean'
            ? resultData.success
            : undefined;
      if (successFlag === false) {
        const apiMessage = extractApiMessage(resultData) || extractApiMessage(result) || '请求失败';
        throw new Error(String(apiMessage));
      }
      const taskId = String(
        resultData.id ||
          resultData.remote_task_id ||
          resultData.remoteTaskId ||
          result.id ||
          resultData.task_id ||
          result.task_id ||
          '',
      );
      if (!taskId) throw new Error('未获取到任务ID');
      addDebug(`任务创建成功: ${taskId}`);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === localId
            ? {
              ...t,
              id: taskId,
              remoteTaskId: taskId,
              status: normalizeStatus(resultData.status || result.status || 'queued'),
            }
            : t,
        ),
      );
      ensurePolling(taskId);
      showToast('任务已提交，等待生成...', 's');
    } catch (err: any) {
      const errMsg = extractApiMessage(err) || extractApiMessage(err?.data) || String(err?.message || err) || '请求失败';
      addDebug(`创建任务失败: ${errMsg}`, true);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === localId ? { ...t, status: 'failed' as TaskStatus, error: errMsg } : t,
        ),
      );
      showToast(errMsg, 'e');
    }
  }, [
    addDebug,
    apiKeyRef,
    connected,
    duration,
    ensurePolling,
    model,
    nodes,
    prompts,
    ratio,
    resolution,
    setNodes,
    setTasks,
    showToast,
    subtitleEnabled,
  ]);

  const onNodeDragStart = (id: string) => (e: React.DragEvent) => {
    dragNodeIdRef.current = id;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onNodeDragEnd = () => {
    setDraggingId(null);
    dragNodeIdRef.current = null;
    setDragOverId(null);
  };
  const onNodeDragOver = (id: string) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragNodeIdRef.current && dragNodeIdRef.current !== id) setDragOverId(id);
  };
  const onNodeDragLeave = () => setDragOverId(null);
  const onNodeDrop = (targetId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const fromId = dragNodeIdRef.current;
    if (!fromId || fromId === targetId) return;
    setNodes((prev) => {
      const fromIdx = prev.findIndex((n) => n.id === fromId);
      const toIdx = prev.findIndex((n) => n.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = prev.slice();
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      return next;
    });
    setDragOverId(null);
  };

  const performClearWorkspace = useCallback(() => {
    setNodes([]);
    setPrompts([{ id: 0, text: '' }]);
    promptSeqRef.current = 1;
    void persistSeedanceWorkspaceState({ allowEmpty: true });
    setClearWorkspaceConfirmOpen(false);
    showToast('已清空', 's');
  }, [setNodes, setPrompts, showToast]);

  const clearAll = useCallback(() => setClearWorkspaceConfirmOpen(true), []);

  const dismissClearWorkspaceConfirm = useCallback(() => setClearWorkspaceConfirmOpen(false), []);

  const counts = useMemo(() => {
    const activeImgs = nodes.filter((n) => n.type === 'image' && n.connected).length;
    const activeVids = nodes.filter((n) => n.type === 'video' && n.connected).length;
    const activeAuds = nodes.filter((n) => n.type === 'audio' && n.connected).length;
    return { activeImgs, activeVids, activeAuds, prompts: prompts.length };
  }, [nodes, prompts]);

  const connectedNodes = useMemo(() => nodes.filter((n) => n.connected), [nodes]);

  const workspacePageBase: WorkspacePageBaseContext = {
    counts,
    prompts,
    nodes,
    draggingId,
    dragOverId,
    promptHighlightRefs,
    promptInputRefs,
    mentionState,
    filteredMentionCandidates,
    connectedNodes,
    model,
    duration,
    ratio,
    resolution,
    subtitleEnabled,
    clearAll,
    addPromptNode,
    removePromptNode,
    handlePromptChangeWithMention,
    identifyPromptMaterials,
    setMentionState,
    onPromptMouseMoveMentionThumb,
    onPromptMouseLeaveMentionThumb,
    applyPromptMention,
    clearPrompt,
    onNodeDragStart,
    onNodeDragEnd,
    onNodeDragOver,
    onNodeDragLeave,
    onNodeDrop,
    removeNode,
    updateNodeName,
    finalizeNodeName,
    toggleConnect,
    onPromptNamedMentionClick,
    onMatPreviewImageError,
    addMatNode,
    setModel,
    setDuration,
    setRatio,
    setResolution,
    setSubtitleEnabled,
    generateVideo,
    mentionThumbHover,
  };

  return {
    workspacePageBase,
    clearWorkspaceConfirmOpen,
    dismissClearWorkspaceConfirm,
    confirmClearWorkspace: performClearWorkspace,
    nodes,
    setNodes,
    addMatNode,
    applyLibAssetToNode,
    mentionThumbHover,
  };
};


const MentionThumbPopover: React.FC<{
  hover: WorkspacePageOutletContext['mentionThumbHover'];
}> = ({ hover }) => {
  if (!hover) return null;
  const popW = 180;
  const popH = 210;
  const pad = 14;
  let left = hover.x + pad;
  let top = hover.y + pad;
  if (typeof window !== 'undefined') {
    left = Math.min(left, window.innerWidth - popW - 10);
    top = Math.min(top, window.innerHeight - popH - 10);
    left = Math.max(10, left);
    top = Math.max(10, top);
  }
  return (
    <div
      className="prompt-mention-thumb-pop"
      style={{ left, top }}
      role="tooltip"
      aria-hidden
    >
      {hover.thumbUrl ? (
        <img
          src={hover.thumbUrl}
          alt=""
          className="prompt-mention-thumb-pop-img"
          draggable={false}
        />
      ) : (
        <div className="prompt-mention-thumb-pop-empty">暂无预览图</div>
      )}
      <div className="prompt-mention-thumb-pop-cap">{hover.label}</div>
    </div>
  );
};


const WorkspacePage: React.FC = () => {
  const clearLibPicker = useSeedanceLibraryStore((s) => s.clearLibPicker);
  const setLibPickerTarget = useSeedanceLibraryStore((s) => s.setLibPickerTarget);
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const openLibPicker = useCallback(
    (nodeId: string) => {
      clearLibPicker();
      setLibPickerTarget(nodeId);
    },
    [clearLibPicker, setLibPickerTarget],
  );

  const {
    workspacePageBase,
    clearWorkspaceConfirmOpen,
    dismissClearWorkspaceConfirm,
    confirmClearWorkspace,
  } = useWorkspacePageController();

  const {
    counts,
    prompts,
    nodes,
    draggingId,
    dragOverId,
    promptHighlightRefs,
    promptInputRefs,
    mentionState,
    filteredMentionCandidates,
    connectedNodes,
    model,
    duration,
    ratio,
    resolution,
    subtitleEnabled,
    clearAll,
    addPromptNode,
    removePromptNode,
    handlePromptChangeWithMention,
    identifyPromptMaterials,
    setMentionState,
    onPromptMouseMoveMentionThumb,
    onPromptMouseLeaveMentionThumb,
    applyPromptMention,
    clearPrompt,
    onNodeDragStart,
    onNodeDragEnd,
    onNodeDragOver,
    onNodeDragLeave,
    onNodeDrop,
    removeNode,
    updateNodeName,
    finalizeNodeName,
    toggleConnect,
    onPromptNamedMentionClick,
    onMatPreviewImageError,
    addMatNode,
    setModel,
    setDuration,
    setRatio,
    setResolution,
    setSubtitleEnabled,
    generateVideo,
    mentionThumbHover,
  } = workspacePageBase;

  const [matPreviewAsset, setMatPreviewAsset] = useState<{ url: string; type: 'image' | 'video' } | null>(null);
  useEffect(() => {
    if (!matPreviewAsset) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMatPreviewAsset(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [matPreviewAsset]);

  const materialLimits = getWorkspaceMaterialLimits(model);
  const maxImgs = materialLimits.images;
  const maxVids = materialLimits.videos;
  const maxAuds = materialLimits.audios;
  const materialLimitExceeded =
    counts.activeImgs > maxImgs || counts.activeVids > maxVids || counts.activeAuds > maxAuds;

  return (
    <>
      <ConfirmModal
        open={clearWorkspaceConfirmOpen}
        title="清空工作台"
        confirmVariant="danger"
        confirmLabel="清空"
        onCancel={dismissClearWorkspaceConfirm}
        onConfirm={confirmClearWorkspace}
      >
        <p>确定清空工作台？所有图片槽与提示词将被重置，此操作无法撤销。</p>
      </ConfirmModal>
      <div className="canvas-wrap" style={{ display: 'block' }}>
        <div className="workspace">
          <div className="workspace-toolbar-row">
            <div className="count-bar" style={{ marginBottom: 0, width: 'auto' }}>
              <div className="cb-item" style={{ color: 'var(--orange)' }}>
                <div className="cb-dot" />
                图片 <b>{counts.activeImgs}/{maxImgs}</b>
              </div>
              <div className="cb-item" style={{ color: 'var(--pink)' }}>
                <div className="cb-dot" />
                提示词 <b>{counts.prompts}/{WORKSPACE_MAX_PROMPTS}</b>
              </div>
              <div className="cb-item" style={{ color: 'var(--green)' }}>
                <div className="cb-dot" />
                视频 <b>{counts.activeVids}/{maxVids}</b>
              </div>
              <div className="cb-item" style={{ color: 'var(--blue)' }}>
                <div className="cb-dot" />
                音频 <b>{counts.activeAuds}/{maxAuds}</b>
              </div>
            </div>
            <button className="btn" onClick={clearAll}>
              🗑️ 清空
            </button>
          </div>
          <div>
            {prompts.map((p, idx) => (
              <div className="node prompt-node" key={p.id}>
                <div className="node-head">
                  <div className="node-ico">🎯</div>
                  <span className="node-title">
                    {idx === 0 ? '提示词' : `提示词 ${idx + 1}`}
                  </span>
                  <button
                    type="button"
                    className="prompt-identify-btn"
                    onClick={() => identifyPromptMaterials(p.id)}
                    title="识别当前提示词中的激活素材名称"
                  >
                    <ScanOutlined />
                    识别素材
                  </button>
                  {idx === 0 ? (
                    <button
                      type="button"
                      onClick={() => addPromptNode()}
                      style={{
                        background: 'var(--purple-dim)',
                        border: '1px solid var(--purple)',
                        borderRadius: 6,
                        padding: '4px 10px',
                        color: 'var(--text-primary)',
                        fontSize: 11,
                        cursor: 'pointer',
                      }}
                    >
                      ➕ 添加
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removePromptNode(p.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontSize: 16,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="node-body">
                  <div className="prompt-ta-wrap">
                    <div
                      className="prompt-ta-highlight"
                      inert
                      ref={(el) => {
                        promptHighlightRefs.current[p.id] = el;
                      }}
                    >
                      <PromptTextHighlight
                        text={p.text}
                        namedMentions={connectedNodes.map((node) => node.name)}
                      />
                    </div>
                    <textarea
                      className="prompt-ta prompt-ta--overlay-input"
                      value={p.text}
                      ref={(el) => {
                        promptInputRefs.current[p.id] = el;
                      }}
                      onChange={(e) =>
                        handlePromptChangeWithMention(
                          p.id,
                          e.target.value,
                          e.currentTarget.selectionStart ?? e.target.value.length,
                        )
                      }
                      onClick={(e) => onPromptNamedMentionClick(p.id, e)}
                      {...promptHighlightOverlayHandlers(
                        () => promptHighlightRefs.current[p.id],
                      )}
                      onBlur={() => {
                        window.setTimeout(() => {
                          setMentionState((s) =>
                            s.promptId === p.id ? { promptId: null, query: '' } : s,
                          );
                        }, 120);
                      }}
                      onMouseMove={onPromptMouseMoveMentionThumb}
                      onMouseLeave={onPromptMouseLeaveMentionThumb}
                      placeholder="描述视频内容…"
                      rows={4}
                    />
                  </div>
                  {mentionState.promptId === p.id && filteredMentionCandidates.length > 0 ? (
                    <div
                      style={{
                        marginTop: 6,
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        background: 'var(--surface-2)',
                        overflow: 'hidden',
                      }}
                    >
                      {filteredMentionCandidates.map((c) => (
                        <button
                          key={c.nodeId}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applyPromptMention(p.id, c)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: '8px 10px',
                            border: 'none',
                            borderBottom: '1px solid var(--border)',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          <span>{c.mention}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{c.name}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div className="prompt-footer" style={{ margin: 0, flex: '1 1 180px', minWidth: 0 }}>
                      <span>
                        点击识别激活素材；提交时映射为图N/视频N/音频N
                      </span>
                      <span>{p.text.length} 字</span>
                    </div>
                    <div className="prompt-actions">
                      <button
                        type="button"
                        onClick={() => clearPrompt(p.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          fontSize: 12,
                          padding: '2px 6px',
                          borderRadius: 4,
                        }}
                        title="清空"
                      >
                        🗑️ 清空
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mat-grid">
            {nodes.filter((n) => n.type === 'image' || n.type === 'video' || n.type === 'audio').map((n) => (
              <MaterialNodeCard
                key={n.id}
                node={n}
                draggingId={draggingId}
                dragOverId={dragOverId}
                onDragStart={onNodeDragStart}
                onDragEnd={onNodeDragEnd}
                onDragOver={onNodeDragOver}
                onDragLeave={onNodeDragLeave}
                onDrop={onNodeDrop}
                onRemove={removeNode}
                onUpdateName={updateNodeName}
                onFinalizeName={finalizeNodeName}
                onToggleConnect={toggleConnect}
                onOpenLibPicker={openLibPicker}
                onPreview={setMatPreviewAsset}
                onPreviewImageError={onMatPreviewImageError}
              />
            ))}
          </div>
        </div>

        <div className="float-toolbar">
          <button className="ft-btn add-img" onClick={() => addMatNode('image')}>
            🖼️ 新增图片
          </button>
          <button className="ft-btn add-vid" onClick={() => addPromptNode()}>
            📝 新增提示词
          </button>
          <button className="ft-btn add-mat-video" onClick={() => addMatNode('video')}>
            🎥 新增视频
          </button>
          <button className="ft-btn add-audio" onClick={() => addMatNode('audio')}>
            🎵 新增音频
          </button>
        </div>
      </div>

      <div className="params">
        <div className="params-section">
          <div className="params-title">🤖 大模型</div>
          <select
            className="p-select p-select--compact"
            value={model}
            onChange={(e) => {
              const newModel = e.target.value;
              const nextLimits = getWorkspaceMaterialLimits(newModel);
              const currentCounts = {
                images: nodes.filter((node) => node.connected && node.type === 'image').length,
                videos: nodes.filter((node) => node.connected && node.type === 'video').length,
                audios: nodes.filter((node) => node.connected && node.type === 'audio').length,
              };
              setModel(newModel);
              const overLimit = ([
                ['图片', currentCounts.images, nextLimits.images],
                ['视频', currentCounts.videos, nextLimits.videos],
                ['音频', currentCounts.audios, nextLimits.audios],
              ] as const).filter(([, count, limit]) => count > limit);
              if (overLimit.length > 0) {
                showToast(
                  `已切换模型，但当前激活素材超出上限：${overLimit
                    .map(([label, count, limit]) => `${label} ${count}/${limit}`)
                    .join('、')}。请修改提示词或取消激活多余素材`,
                  'e',
                );
              }
              if (
                newModel === 'doubao-seedance-2-0-mini-260615' ||
                newModel === 'doubao-seedance-2-0-fast-260128' ||
                newModel === 'doubao-seedance-2-5-260628'
              ) {
                if (resolution !== '480p' && resolution !== '720p') {
                  setResolution('720p');
                }
              }
              const maxDuration = newModel === 'doubao-seedance-2-5-260628' ? 30 : 15;
              if (duration > maxDuration) {
                setDuration(maxDuration);
              }
            }}
          >
            <option value="doubao-seedance-2-0-260128">Seedance 2.0 标准版</option>
            <option value="doubao-seedance-2-0-mini-260615">Seedance 2.0 mini</option>
            <option value="doubao-seedance-2-0-fast-260128">Seedance 2.0 fast</option>
            <option value="doubao-seedance-2-5-260628">Seedance 2.5</option>
          </select>
        </div>
        <div className="params-section">
          <div className="params-title">视频设置</div>
          <div className="p-row">
            <div className="p-label">时长</div>
            <input
              className="p-slider"
              type="range"
              min={5}
              max={model === 'doubao-seedance-2-5-260628' ? 30 : 15}
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value, 10))}
            />
            <div className="p-val">
              <span>{duration}</span> 秒
            </div>
          </div>
          <div className="p-row">
            <div className="p-label">画面比例</div>
            <select
              className="p-select"
              value={ratio}
              onChange={(e) => setRatio(e.target.value)}
            >
              <option value="16:9">16:9（默认）</option>
              <option value="4:3">4:3</option>
              <option value="1:1">1:1</option>
              <option value="3:4">3:4</option>
              <option value="9:16">9:16</option>
              <option value="21:9">21:9</option>
            </select>
          </div>
          <div className="p-row">
            <div className="p-label">分辨率</div>
            <select
              className="p-select"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            >
              <option value="720p">720p 推荐</option>
              <option value="480p">480p 快速</option>
              {model === 'doubao-seedance-2-0-260128' && (
                <>
                  <option value="1080p">1080p 高清</option>
                  <option value="4k">4k 超清</option>
                </>
              )}
            </select>
          </div>
          <div className="p-row p-row--switch">
            <div className="p-label">字幕</div>
            <label className="p-switch">
              <input
                type="checkbox"
                checked={subtitleEnabled}
                onChange={(e) => setSubtitleEnabled(e.target.checked)}
              />
              <span className="p-switch-track" aria-hidden />
              <span className="p-switch-text">{subtitleEnabled ? '开启' : '关闭'}</span>
            </label>
          </div>
        </div>
        <div className="params-section params-section--modules">
          <div className="params-title">已激活模块</div>
          <div className="params-modules-body">
            {connectedNodes.length ? (
              connectedNodes.map((n) => (
                <div key={n.id} className="params-module-item">
                  {ICONS[n.type]} {n.name}
                </div>
              ))
            ) : (
              <span>暂无激活模块</span>
            )}
          </div>
        </div>
        <button
          className="gen-btn"
          onClick={generateVideo}
          disabled={materialLimitExceeded}
          title={materialLimitExceeded ? '当前模型素材数量已超上限，请取消激活多余素材或切换模型' : undefined}
        >
          🚀 开始生成
        </button>
      </div>
      <MentionThumbPopover hover={mentionThumbHover} />
      {matPreviewAsset ? (
        <div
          className="ws-mat-lightbox"
          role="presentation"
          onClick={() => setMatPreviewAsset(null)}
        >
          <button
            type="button"
            className="ws-mat-lightbox-close"
            aria-label="关闭"
            onClick={() => setMatPreviewAsset(null)}
          >
            ✕
          </button>
          <div
            className="ws-mat-lightbox-inner"
            role="dialog"
            aria-modal="true"
            aria-label="素材预览"
            onClick={(e) => e.stopPropagation()}
          >
            {matPreviewAsset.type === 'image' ? (
              <img src={matPreviewAsset.url} alt="" referrerPolicy="no-referrer" />
            ) : (
              <video
                src={matPreviewAsset.url}
                controls
                autoPlay
                muted
                playsInline
                style={{ display: 'block' }}
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default WorkspacePage;
